/**
 * Hub step rows (Strategy / Preferences / Monthly SIP) + completion helpers.
 *
 * Open this file to change: step labels, click destinations, or “is this step done?”.
 * Not for: portfolio card chrome or submit animation.
 */
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { toPascalCaseWithSpaces } from '../../../../utils/utils'
import { EventNames } from '../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../utils/appBridge'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../analytics/expertReviewEvents'
import { formatRupeeLabel } from '../../plans-v4/display-values/lumpsumPlanAmount'
import {
  isQuestionnaireStatusDone,
  QuestionnaireStatus,
  StrategyQuestionnaireStageId
} from '../shared/types/questionnaire.types'
import type { StatusQuestionnaireApiResponse } from '../types/statusQuestionnaire.types'
import { useStrategyQuestionnaireContext } from '../strategy-questionnaire/flow-engine/StrategyQuestionnaireProvider'
import { usePreferencesContext } from '../preferences/flow-engine/PreferencesProvider'
import { useExpertReviewStatusContext } from '../providers/ExpertReviewStatusContext'
import {
  EXPERT_REVIEW_CAME_FROM,
  useExpertReviewNavigation
} from '../navigation/expertReviewNavigation'

export enum AllocationPlanStepId {
  Strategy = 'strategy',
  Preferences = 'preferences',
  MonthlySip = 'monthly_sip'
}

export function isStrategyStepCompleted(
  statusData: StatusQuestionnaireApiResponse | undefined
): boolean {
  const strategy = statusData?.strategyQuestionnaire
  // Strategy step stays pending until questionnaire is done AND risk profile exists
  return isQuestionnaireStatusDone(strategy?.status) && strategy?.riskProfile != null
}

export function isPreferencesStepCompleted(
  statusData: StatusQuestionnaireApiResponse | undefined
): boolean {
  return isQuestionnaireStatusDone(statusData?.preferenceQuestionnaire?.status)
}

export function isMonthlySipStepCompleted(
  statusData: StatusQuestionnaireApiResponse | undefined
): boolean {
  const strategy = statusData?.strategyQuestionnaire
  const sipStageStatus = strategy?.stageProgress?.find(
    (stage) => stage.stageId === StrategyQuestionnaireStageId.SipAllocation
  )?.status
  if (sipStageStatus != null) {
    return isQuestionnaireStatusDone(sipStageStatus)
  }
  return strategy?.sipAllocationPlanRequested != null
}

/** First incomplete step in fixed order: Strategy → Preferences → Monthly SIP. */
export function getHighlightedStepId(
  statusData: StatusQuestionnaireApiResponse | undefined
): AllocationPlanStepId | '' {
  if (!isStrategyStepCompleted(statusData)) return AllocationPlanStepId.Strategy
  if (!isPreferencesStepCompleted(statusData)) return AllocationPlanStepId.Preferences
  if (!isMonthlySipStepCompleted(statusData)) return AllocationPlanStepId.MonthlySip
  return ''
}

export function areAllStepsCompleted(
  statusData: StatusQuestionnaireApiResponse | undefined
): boolean {
  return (
    isStrategyStepCompleted(statusData) &&
    isPreferencesStepCompleted(statusData) &&
    isMonthlySipStepCompleted(statusData)
  )
}

export function StrategyStepCard() {
  const navigation = useExpertReviewNavigation()
  const { statusData } = useExpertReviewStatusContext()
  const { riskProfile, hasReachedReviewOnce } = useStrategyQuestionnaireContext()
  const isCompleted = isStrategyStepCompleted(statusData)

  let statusText = 'Pending'
  if (isCompleted) {
    statusText = toPascalCaseWithSpaces(
      (riskProfile ?? statusData?.strategyQuestionnaire?.riskProfile) ?? ''
    )
  }

  function onCardClick() {
    const strategy = statusData?.strategyQuestionnaire
    const status = strategy?.status
    const hasRiskProfile = (riskProfile ?? strategy?.riskProfile) != null

    // Done with risk profile — open the reveal screen
    if (isQuestionnaireStatusDone(status) && hasRiskProfile) {
      navigation.goToRiskProfile({ cameFrom: EXPERT_REVIEW_CAME_FROM.HUB })
      return
    }

    // Done but no risk profile — review (continue to risk from review CTA)
    if (isQuestionnaireStatusDone(status) && !hasRiskProfile) {
      navigation.goToStrategyReview({ cameFrom: EXPERT_REVIEW_CAME_FROM.HUB })
      return
    }

    // Finished answering once this session — resume on review (even if status still in progress)
    if (hasReachedReviewOnce) {
      navigation.goToStrategyReview({ cameFrom: EXPERT_REVIEW_CAME_FROM.HUB })
      return
    }

    // Not initiated — start questionnaire with intro / stage animations
    if (status === QuestionnaireStatus.NOT_INITIATED) {
      navigation.goToStrategyQuestionnaire({
        showIntro: true,
        cameFrom: EXPERT_REVIEW_CAME_FROM.HUB
      })
      return
    }

    // In progress (pending) — resume questionnaire without intro
    if (status === QuestionnaireStatus.IN_PROGRESS) {
      navigation.goToStrategyQuestionnaire({
        showIntro: false,
        cameFrom: EXPERT_REVIEW_CAME_FROM.HUB
      })
      return
    }

    // Unknown / missing status — safest resume is questionnaire without intro
    navigation.goToStrategyQuestionnaire({
      showIntro: false,
      cameFrom: EXPERT_REVIEW_CAME_FROM.HUB
    })
  }

  return (
    <AllocationPlanStepCard
      id={AllocationPlanStepId.Strategy}
      title="Strategy"
      statusText={statusText}
      isCompleted={isCompleted}
      onCardClick={onCardClick}
      iconHtml={STRATEGY_STEP_ICON}
    />
  )
}

export function PreferencesStepCard() {
  const navigation = useExpertReviewNavigation()
  const { statusData } = useExpertReviewStatusContext()
  const { hasReachedReviewOnce } = usePreferencesContext()
  const isCompleted = isPreferencesStepCompleted(statusData)

  let statusText = 'Pending'
  if (isCompleted) {
    const preferenceCount = statusData?.preferenceQuestionnaire?.preferenceCount ?? 0
    if (preferenceCount === 1) statusText = '1 saved'
    else if (preferenceCount > 1) statusText = `${preferenceCount} saved`
  }

  function onCardClick() {
    if (isCompleted || hasReachedReviewOnce) {
      navigation.goToPreferencesReview({ cameFrom: EXPERT_REVIEW_CAME_FROM.HUB })
      return
    }
    // Incomplete, never reached review — pick preference types before questionnaire
    navigation.goToPreferencesList({ cameFrom: EXPERT_REVIEW_CAME_FROM.HUB })
  }

  return (
    <AllocationPlanStepCard
      id={AllocationPlanStepId.Preferences}
      title="Preferences"
      statusText={statusText}
      isCompleted={isCompleted}
      onCardClick={onCardClick}
      iconHtml={PREFERENCES_STEP_ICON}
    />
  )
}

export function MonthlySipStepCard() {
  const { statusData } = useExpertReviewStatusContext()
  const { openHubSipEdit } = useStrategyQuestionnaireContext()
  const isCompleted = isMonthlySipStepCompleted(statusData)

  let statusText = 'Pending'
  let statusSubtext: string | undefined
  if (isCompleted) {
    const monthlySipAmount = statusData?.strategyQuestionnaire?.monthlySipAmount
    if (statusData?.strategyQuestionnaire?.sipAllocationPlanRequested === true) {
      if (typeof monthlySipAmount === 'number' && Number.isFinite(monthlySipAmount)) {
        statusText = `Yes, ${formatSipAmountLabel(monthlySipAmount)}`
      } else {
        statusText = 'Yes'
      }
    } else {
      statusText = 'No'
      if (typeof monthlySipAmount === 'number' && Number.isFinite(monthlySipAmount)) {
        statusSubtext = `An SIP plan will be prepared with a total SIP of ${formatSipAmountLabel(monthlySipAmount)}`
      }
    }
  }

  return (
    <AllocationPlanStepCard
      id={AllocationPlanStepId.MonthlySip}
      title="Monthly SIP"
      statusText={statusText}
      statusSubtext={statusSubtext}
      isCompleted={isCompleted}
      onCardClick={openHubSipEdit}
      iconHtml={MONTHLY_SIP_STEP_ICON}
    />
  )
}

function formatSipAmountLabel(amount: number): string {
  return `${formatRupeeLabel({ rawAmount: amount })}/mo`
}

const STEP_ICON_CLASS = 'flex-shrink-0 w-10 h-10 [&>svg]:w-full [&>svg]:h-full'
const PENDING_STATUS_COLOR = '#CC5C2D'

const RIGHT_CHEVRON_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="40" viewBox="0 0 10 40" fill="none">
  <path d="M2 14L8 20L2 26" stroke="#D1D6D6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const STRATEGY_STEP_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
  <path d="M18.8244 6.8541C19.5254 6.34481 20.4746 6.34481 21.1756 6.8541L32.1392 14.8197C32.8402 15.3289 33.1335 16.2317 32.8658 17.0557L28.678 29.9443C28.4103 30.7683 27.6424 31.3262 26.7759 31.3262H13.2241C12.3576 31.3262 11.5897 30.7683 11.322 29.9443L7.13424 17.0557C6.86649 16.2317 7.1598 15.3289 7.86078 14.8197L18.8244 6.8541Z" stroke="#232323" style="mix-blend-mode:hard-light" stroke-width="2"/>
  <path d="M18.5734 18.0365C19.4241 17.4184 20.5759 17.4184 21.4266 18.0365C22.2772 18.6545 22.6332 19.75 22.3083 20.75C21.9833 21.75 21.0515 22.4271 20 22.4271C18.9485 22.4271 18.0167 21.75 17.6917 20.75C17.3668 19.75 17.7228 18.6545 18.5734 18.0365Z" fill="#3D3D3D"/>
</svg>`

const PREFERENCES_STEP_ICON = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M30.2505 30.2504C30.2505 30.7446 30.0541 31.2187 29.7046 31.5681C29.3552 31.9176 28.8811 32.114 28.3869 32.114H11.6141C11.1199 32.114 10.6458 31.9176 10.2963 31.5681C9.94684 31.2187 9.75049 30.7446 9.75049 30.2504V9.75035C9.75049 9.25608 9.94684 8.78207 10.2963 8.43256C10.6458 8.08307 11.1199 7.88672 11.6141 7.88672H22.024C22.5183 7.88672 22.9923 8.08307 23.3418 8.43256L29.7046 14.7954C30.0541 15.1449 30.2505 15.6189 30.2505 16.1132V30.2504Z" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M21.0415 18.8359H25.7006" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M21.0415 25.7656H25.7006" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.8345 25.6601L15.396 27.2217L17.9985 23.5781" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.8345 18.6133L15.396 20.1748L17.9985 16.5312" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`

const MONTHLY_SIP_STEP_ICON = `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M14.4287 19.0713H25.5716" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M14.4287 24.6436H20.0001" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M10.7146 11.6436C10.222 11.6436 9.74965 11.8392 9.40136 12.1875C9.05309 12.5358 8.85742 13.0081 8.85742 13.5007V29.2864C8.85742 29.7789 9.05309 30.2514 9.40136 30.5996C9.74965 30.9478 10.222 31.1436 10.7146 31.1436H29.286C29.7785 31.1436 30.251 30.9478 30.5992 30.5996C30.9474 30.2514 31.1431 29.7789 31.1431 29.2864V13.5007C31.1431 13.0081 30.9474 12.5358 30.5992 12.1875C30.251 11.8392 29.7785 11.6436 29.286 11.6436H10.7146Z" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.5 8.85742V14.4289" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M26.5 8.85742V14.4289" stroke="#3D3D3D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`

/** Shared step row chrome — icon, title, status, chevron. */
export function AllocationPlanStepCard({
  id,
  title,
  statusText,
  statusSubtext,
  isCompleted,
  onCardClick,
  iconHtml
}: {
  id: AllocationPlanStepId
  title: string
  statusText: string
  statusSubtext?: string
  isCompleted: boolean
  onCardClick: () => void
  iconHtml: string
}) {
  return (
    <div
      id={id}
      onClick={() => {
        lightImpactVibrate()
        trackEvent({
          eventName: EventNames.UserClicked,
          payload: {
            ScreenName: ExpertReviewScreen.Hub,
            eventId: ExpertReviewEventIds.HubStep,
            buttonLabel: title
          }
        })
        onCardClick()
      }}
      className="flex cursor-pointer items-center justify-between gap-4 px-5 py-5"
    >
      <div className="flex min-w-0 items-start gap-4">
        <span className={`mt-0.5 ${STEP_ICON_CLASS}`} aria-hidden>
          <RenderHTML innerHTMLText={iconHtml} className="w-full h-full" />
        </span>
        <div className="min-w-0 flex-1">
          <h2
            className={
              isCompleted
                ? 'text-[14px] font-[400] leading-[130%] text-[#6A6E6E]'
                : 'text-[18px] font-[500] leading-[120%] text-[#232323]'
            }
            test-locator={`allocation-plan-step-${id}-title`}
          >
            {title}
          </h2>
          <p
            className={
              isCompleted
                ? 'pt-1 text-[18px] font-[500] leading-[120%] text-[#232323]'
                : 'pt-[3px] text-[14px] font-[400] leading-[130%]'
            }
            style={isCompleted ? undefined : { color: PENDING_STATUS_COLOR }}
            test-locator={`allocation-plan-step-${id}-status`}
          >
            {statusText}
          </p>
          {isCompleted && statusSubtext ? (
            <p className="pt-1 text-[13px] font-[400] leading-[150%] text-[#6A6E6E]">
              {statusSubtext}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex-shrink-0" aria-hidden>
        <RenderHTML innerHTMLText={RIGHT_CHEVRON_ICON} />
      </div>
    </div>
  )
}
